// ============================================================================
// apps/web/src/lib/auth-context.tsx
// ----------------------------------------------------------------------------
// Contexte React qui suit la session Supabase + le profil + les capabilities
// effectives. Source de vérité pour gate l'UI :
//
//   const { profile, hasCapability, signOut } = useAuth();
//   if (!hasCapability(Capability.PERMISSIONS_MANAGE)) return <Forbidden />;
//
// Initialise au mount : récupère la session courante, écoute les changements
// auth, fetch /api/me pour avoir le profile + capabilities.
// ============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from './supabase';
import { fetchMe, type MeResponse } from './worker';
import { Capability, type CapabilityKey } from '@magic/shared';
import type { Session } from '@supabase/supabase-js';

type AuthState = {
  status: 'loading' | 'unauthenticated' | 'authenticated';
  session: Session | null;
  me: MeResponse | null;
  error: string | null;
};

type AuthContextValue = AuthState & {
  // true après un clic sur un lien de réinitialisation (événement Supabase
  // PASSWORD_RECOVERY) → force le passage par /change-password quel que soit
  // l'atterrissage (même si le redirect retombe sur la racine).
  isRecovery: boolean;
  hasCapability: (cap: CapabilityKey) => boolean;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    session: null,
    me: null,
    error: null,
  });
  const [isRecovery, setIsRecovery] = useState(false);

  const loadMe = useCallback(async (session: Session | null) => {
    if (!session) {
      setState({ status: 'unauthenticated', session: null, me: null, error: null });
      return;
    }
    try {
      const me = await fetchMe();
      setState({ status: 'authenticated', session, me, error: null });
    } catch (err) {
      console.error('[auth] fetchMe failed', err);
      setState({
        status: 'authenticated',
        session,
        me: null,
        error: err instanceof Error ? err.message : 'Erreur inconnue',
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      void loadMe(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Lien « mot de passe oublié » cliqué → session recovery : on force le
      // passage par /change-password (cf. isRecovery + garde côté routes).
      if (event === 'PASSWORD_RECOVERY') setIsRecovery(true);
      void loadMe(session);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [loadMe]);

  const hasCapability = useCallback(
    (cap: CapabilityKey): boolean => {
      const caps = state.me?.capabilities ?? [];
      if (caps.includes(Capability.ADMIN_ALL)) return true;
      return caps.includes(cap);
    },
    [state.me],
  );

  const signOut = useCallback(async () => {
    setIsRecovery(false);
    await supabase.auth.signOut();
  }, []);

  const refreshMe = useCallback(async () => {
    await loadMe(state.session);
  }, [loadMe, state.session]);

  return (
    <AuthContext.Provider value={{ ...state, isRecovery, hasCapability, signOut, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
